import {
  CheckOutlined,
  EyeInvisibleOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Space, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import type { FormsQuery } from '../../../graphql/generated/graphql.ts';
import { tableActionCell } from '../../../styles/globalCss';
import { FORMS_QUERY } from './Forms.gql';
import { CreateFormModal } from './forms/CreateFormModal';
import { FormDocumentModal } from './forms/FormDocumentModal';
import { FormEditorDrawer } from './forms/FormEditorDrawer';

export type Form = NonNullable<FormsQuery['labForms']>[number];

export const Forms: React.FC = () => {
  const { token } = theme.useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editForm, setEditForm] = useState<Form | null>(null);
  const [documentForm, setDocumentForm] = useState<Form | null>(null);

  const { data, loading, refetch } = useQuery(FORMS_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<Form> = [
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 150,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Überschrift',
      dataIndex: 'header',
      key: 'header',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => a.header.localeCompare(b.header),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Felder',
      dataIndex: 'fields',
      key: 'fields',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => a.fields.length - b.fields.length,
      render: value => value.length,
    },
    {
      title: 'Dokument',
      dataIndex: 'hasDocument',
      key: 'hasDocument',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => Number(a.hasDocument) - Number(b.hasDocument),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Drucken',
      dataIndex: 'printRequired',
      key: 'printRequired',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => Number(a.printRequired) - Number(b.printRequired),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Vor Senden drucken',
      dataIndex: 'printBeforeSend',
      key: 'printBeforeSend',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => Number(a.printBeforeSend) - Number(b.printBeforeSend),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Kopien',
      dataIndex: 'printCopies',
      key: 'printCopies',
      ellipsis: true,
      width: 90,
      sorter: (a, b) => a.printCopies - b.printCopies,
    },
    {
      title: 'Elekt. übertragen',
      dataIndex: 'pickup',
      key: 'pickup',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => Number(a.pickup) - Number(b.pickup),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: (
        <>
          Direkt{' '}
          <Tooltip title="Ohne Anforderung mit Patienten- und Formulardaten erstellbar">
            <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      dataIndex: 'direct',
      key: 'direct',
      ellipsis: true,
      width: 90,
      sorter: (a, b) => Number(a.direct) - Number(b.direct),
      render: (_, record) =>
        record.direct ? (
          <Space>
            <CheckOutlined />
            {!!record.doctors.length && <EyeInvisibleOutlined />}
          </Space>
        ) : null,
    },
    {
      title: (
        <>
          Wiederherstellen{' '}
          <Tooltip title="Beim Wiederholen einer Anforderung die Formularfelder wiederherstellen">
            <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      dataIndex: 'restore',
      key: 'restore',
      ellipsis: true,
      width: 170,
      sorter: (a, b) => Number(a.restore) - Number(b.restore),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      ellipsis: true,
      width: '50px',
      className: tableActionCell,
      render: (_, record) => (
        <Tooltip title="Dokument ändern">
          <Button
            icon={<FilePdfOutlined />}
            type="text"
            onClick={e => {
              e.stopPropagation();
              setDocumentForm(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const filteredForms: Form[] = useMemo(
    () => filterByTokens(data?.labForms ?? [], debouncedSearch, [f => f.name, f => f.header]),
    [data, debouncedSearch]
  );

  return (
    <MainContent size="full">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          autoFocus
          allowClear
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Bezeichnung oder Überschrift">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalOpen(true)}>
          Neues Formular
        </Button>
      </div>
      <Table<Form>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredForms}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Formularen`,
        }}
        onRow={record => ({ onClick: () => setEditForm(record), style: { cursor: 'pointer' } })}
        loading={loading}
        columns={columns}
      />
      <CreateFormModal open={createModalOpen} onSuccess={() => refetch()} onClose={() => setCreateModalOpen(false)} labId={labId ?? ''} />
      <FormEditorDrawer form={editForm} onClose={() => setEditForm(null)} onSuccess={refetch} doctors={data?.doctors ?? []} />
      <FormDocumentModal
        form={documentForm}
        onClose={() => {
          refetch();
          setDocumentForm(null);
        }}
      />
    </MainContent>
  );
};
